import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";
import CTASection from "@/components/shared/CTASection";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";
import PageSEO from "@/components/shared/PageSEO";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { 
  Heart, 
  TrendingUp, 
  BookOpen, 
  Leaf, 
  Users, 
  Building2,
  MapPin,
  Clock,
  Briefcase,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

const cultureValues = [
  {
    icon: Heart,
    title: "Purpose-Driven Work",
    description: "Every day, you'll contribute to building a more sustainable future for our communities.",
    color: "eco-green",
  },
  {
    icon: Users,
    title: "Collaborative Spirit",
    description: "We believe the best ideas come from diverse teams working together toward common goals.",
    color: "sky-blue",
  },
  {
    icon: TrendingUp,
    title: "Growth Mindset",
    description: "We invest in your development because your growth drives our collective success.",
    color: "primary",
  },
  {
    icon: Leaf,
    title: "Environmental Impact",
    description: "Be part of a team that's actively making a difference in reducing carbon footprints.",
    color: "purple",
  },
];

const benefits = [
  {
    icon: Heart,
    title: "Health & Wellness",
    description: "Comprehensive health insurance for you and your family, plus wellness programs.",
    color: "eco-green",
  },
  {
    icon: TrendingUp,
    title: "Career Growth",
    description: "Clear career paths with regular performance reviews and promotion opportunities.",
    color: "sky-blue",
  },
  {
    icon: BookOpen,
    title: "Learning & Development",
    description: "Access to training programs, certifications, and educational allowances.",
    color: "primary",
  },
  {
    icon: Leaf,
    title: "Sustainability Perks",
    description: "Electric vehicle allowances and incentives for sustainable commuting.",
    color: "purple",
  },
  {
    icon: Users,
    title: "Team Culture",
    description: "Regular team events, celebrations, and a supportive work environment.",
    color: "eco-green",
  },
  {
    icon: Building2,
    title: "Modern Workspace",
    description: "State-of-the-art facilities designed for collaboration and productivity.",
    color: "sky-blue",
  },
];

const openings = [
  {
    title: "Sales Manager",
    department: "Sales",
    location: "Jeddah, Saudi Arabia",
    type: "Full-time",
    description: "Lead our B2B sales efforts and build relationships with key enterprise clients.",
    requirements: ["5+ years sales experience", "Automotive/mobility background preferred", "Arabic & English fluency"],
  },
  {
    title: "Technical Support Engineer",
    department: "Operations",
    location: "Jeddah, Saudi Arabia",
    type: "Full-time",
    description: "Provide technical support for our fleet of electric vehicles and ensure customer satisfaction.",
    requirements: ["3+ years technical experience", "EV/automotive knowledge", "Customer service skills"],
  },
  {
    title: "Marketing Coordinator",
    department: "Marketing",
    location: "Jeddah, Saudi Arabia",
    type: "Full-time",
    description: "Support marketing campaigns and help grow our brand presence across the GCC region.",
    requirements: ["2+ years marketing experience", "Digital marketing skills", "Creative portfolio"],
  },
  {
    title: "Fleet Operations Specialist",
    department: "Operations",
    location: "Riyadh, Saudi Arabia",
    type: "Full-time",
    description: "Manage day-to-day fleet operations and coordinate with clients for optimal service delivery.",
    requirements: ["3+ years operations experience", "Fleet management background", "Strong organizational skills"],
  },
];

const colorMap: Record<string, { bg: string; border: string; iconBg: string; glow: string }> = {
  "eco-green": {
    bg: "from-eco-green/10 to-teal-bright/5",
    border: "border-eco-green/20 hover:border-eco-green/40",
    iconBg: "from-eco-green to-teal-bright",
    glow: "group-hover:shadow-eco-green/20",
  },
  "sky-blue": {
    bg: "from-sky-blue/10 to-purple/5",
    border: "border-sky-blue/20 hover:border-sky-blue/40",
    iconBg: "from-sky-blue to-purple",
    glow: "group-hover:shadow-sky-blue/20",
  },
  "primary": {
    bg: "from-primary/10 to-amber-glow/5",
    border: "border-primary/20 hover:border-primary/40",
    iconBg: "from-primary to-amber-glow",
    glow: "group-hover:shadow-primary/20",
  },
  "purple": {
    bg: "from-purple/10 to-rose/5",
    border: "border-purple/20 hover:border-purple/40",
    iconBg: "from-purple to-rose",
    glow: "group-hover:shadow-purple/20",
  },
};

const Careers = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Careers in Micro Mobility Saudi Arabia"
        description="Join SDC تطوير الاستدامة and build a career in micro mobility. Job openings in Jeddah & Riyadh - sales, operations, fleet management. وظائف في قطاع التنقل الصغير بالسعودية."
        keywords="careers SDC, وظائف تطوير الاستدامة, jobs micro mobility Saudi Arabia, mobility careers Jeddah, electric vehicle jobs KSA, وظائف مركبات كهربائية, fleet management careers Saudi, sustainable jobs Riyadh"
      />
      <Navbar />
      
      <PageHero
        title="Join Our Team"
        subtitle="Build a Sustainable Career"
        description="Be part of a mission-driven team transforming urban mobility across the Middle East. We're looking for passionate individuals ready to make an impact."
        variant="vibrant"
      />

      <Breadcrumbs />

      {/* Culture Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -left-40 top-1/4 w-80 h-80 rounded-full bg-eco-green/10 blur-3xl animate-pulse" />
          <div className="absolute -right-40 bottom-1/4 w-80 h-80 rounded-full bg-sky-blue/10 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Our Culture"
            title="Where Purpose Meets Passion"
            description="At SDC, we believe that meaningful work and great culture go hand in hand."
            badgeColor="green"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cultureValues.map((value, index) => {
              const colors = colorMap[value.color];
              return (
                <div
                  key={index}
                  className={`group relative text-center p-6 rounded-2xl bg-gradient-to-br ${colors.bg} backdrop-blur-sm border ${colors.border} transition-all duration-300 hover:scale-105 hover:shadow-xl ${colors.glow}`}
                >
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${colors.iconBg} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-transparent to-secondary/30" />
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-eco-green/30' : 'bg-sky-blue/30'}`}
              style={{
                left: `${10 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Perks & Benefits"
            title="What We Offer"
            badgeColor="blue"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const colors = colorMap[benefit.color];
              return (
                <div
                  key={index}
                  className={`group p-6 rounded-2xl bg-gradient-to-br ${colors.bg} backdrop-blur-sm border ${colors.border} transition-all duration-300 hover:scale-105 hover:shadow-xl ${colors.glow}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors.iconBg} flex items-center justify-center mb-4 shadow-lg`}>
                    <benefit.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision 2030 Alignment */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto p-8 rounded-2xl bg-gradient-to-r from-primary/15 via-eco-green/10 to-sky-blue/15 border border-primary/30 backdrop-blur-sm shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary via-eco-green to-sky-blue flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-white font-bold text-2xl">2030</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Vision 2030 Aligned Partner</h3>
                <p className="text-muted-foreground">
                  Working at SDC means being part of Saudi Arabia's transformational journey. 
                  Our commitment to sustainable mobility directly supports the Kingdom's environmental 
                  and economic objectives under Vision 2030.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-transparent to-secondary/30" />
        
        {/* Animated background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -right-40 top-1/3 w-80 h-80 rounded-full bg-purple/10 blur-3xl animate-pulse" />
          <div className="absolute -left-40 bottom-1/3 w-80 h-80 rounded-full bg-primary/10 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <VibrantSectionHeader
            badge="Opportunities"
            title="Current Openings"
            badgeColor="gold"
          />
          <div className="max-w-4xl mx-auto space-y-6">
            {openings.map((job, index) => (
              <div
                key={index}
                className="group p-6 rounded-2xl bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-sm border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">{job.title}</h3>
                    <p className="text-muted-foreground">{job.department}</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-eco-green/20 to-teal-bright/10 text-sm text-eco-green border border-eco-green/20">
                      <MapPin className="w-3 h-3" />
                      {job.location}
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-sky-blue/20 to-purple/10 text-sm text-sky-blue border border-sky-blue/20">
                      <Clock className="w-3 h-3" />
                      {job.type}
                    </span>
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">{job.description}</p>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <ul className="flex flex-wrap gap-2">
                    {job.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CheckCircle2 className="w-3 h-3 text-eco-green" />
                        {req}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    size="sm" 
                    asChild
                    className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300"
                  >
                    <a href={`mailto:careers@sdc.sa?subject=${encodeURIComponent(`Application for ${job.title} - ${job.department}`)}&body=${encodeURIComponent(`Dear Hiring Team,\n\nI am writing to express my interest in the ${job.title} position in the ${job.department} department.\n\nPlease find my CV attached.\n\nBest regards,`)}`}>
                      Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Application */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-eco-green/10 blur-3xl" />
          <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-sky-blue/10 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary via-eco-green to-sky-blue flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Don't See the Right Fit?
            </h2>
            <p className="text-muted-foreground mb-8">
              We're always looking for talented individuals to join our team. Send us your resume 
              and tell us how you can contribute to our mission.
            </p>
            <Button 
              variant="outline" 
              size="lg" 
              asChild
              className="border-primary/50 hover:border-primary hover:bg-primary/10 transition-all duration-300"
            >
              <a href={`mailto:careers@sdc.sa?subject=${encodeURIComponent('General Application - SDC Careers')}&body=${encodeURIComponent(`Dear Hiring Team,\n\nI am writing to express my interest in joining SDC. Although I don't see a specific position that matches my profile, I believe my skills and experience could be valuable to your team.\n\nPlease find my CV attached.\n\nBest regards,`)}`}>
                Submit General Application <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Make an Impact?"
        description="Join a team that's transforming urban mobility and building a sustainable future."
        primaryButtonText="View Openings"
        primaryButtonLink="#openings"
        secondaryButtonText="Contact HR"
        secondaryButtonLink="/contact"
      />

      <Footer />
    </div>
  );
};

export default Careers;
